/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePeriod } from '@/pages/reports/PeriodContext';
import { useReportSnapshot } from './useReportSnapshot';
import { computeBudgetSummary } from '@/lib/analytics/budgetSummary';
import { BudgetSummary } from '@/lib/analytics/types';

export function useMonthlyBudgetAnalytics() {
  const { user } = useAuth();
  const { period } = usePeriod();

  // Derive year/month from the period's dateFrom
  const [yearStr, monthStr] = period.dateFrom.split('-');
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);

  const snapshot = useReportSnapshot({
    userId: user?.id ?? '',
    periodType: 'monthly',
    periodStart: period.dateFrom,
    periodEnd: period.dateTo,
  });

  return useQuery({
    queryKey: ['monthly-budget-analytics', user?.id, period.dateFrom, period.dateTo],
    queryFn: async (): Promise<BudgetSummary | null> => {
      // Use snapshot if available
      if (snapshot.data) {
        const { totalIncome: _i, totalExpenses, netSavings: _n, categoryBreakdown } = snapshot.data;
        // Build a minimal BudgetSummary from snapshot category breakdown
        const rows = categoryBreakdown.map((cat) => ({
          categoryId: cat.categoryId,
          categoryName: cat.categoryName,
          subCategoryId: cat.subCategoryId,
          subCategoryName: cat.subCategoryName,
          budgeted: 0,
          actual: cat.total,
          deviation: cat.total,
          isOverBudget: false,
          currency: 'KES'
        }));
        return {
          totalBudgeted: 0,
          totalActual: totalExpenses,
          totalDeviation: totalExpenses,
          rows,
        };
      }

      // Fall back to live query: find monthly budget for this period
      const { data: budgets, error: budgetError } = await (supabase as any)
        .from('monthly_budgets')
        .select('id')
        .eq('user_id', user!.id)
        .eq('year', year)
        .eq('month', month);

      if (budgetError) throw budgetError;
      if (!budgets || budgets.length === 0) return null;

      const budgetId = budgets[0].id;

      // Fetch budget items with category names
      const { data: items, error: itemsError } = await (supabase as any)
        .from('monthly_budget_items')
        .select('category_id, sub_category_id, amount, currency, categories(name), sub_categories(name)')
        .eq('monthly_budget_id', budgetId);

      if (itemsError) throw itemsError;

      // Fetch transactions for the period
      const { data: transactions, error: txError } = await (supabase as any)
        .from('transactions')
        .select('category_id, amount, currency, type, sub_category_id')
        .eq('user_id', user!.id)
        .gte('transaction_date', period.dateFrom)
        .lte('transaction_date', period.dateTo)
        .eq('type', 'expense');

      if (txError) throw txError;

      const budgetItems = (items ?? []).map((item: any) => ({
        category_id: item.category_id,
        sub_category_id: item.sub_category_id,
        category_name: item.categories?.name ?? 'Unknown',
        sub_category_name: item.sub_categories?.name ?? 'Unknown',
        amount: item.amount,
        currency: item.currency,
      }));

      return computeBudgetSummary(budgetItems, transactions ?? []);
    },
    enabled: !!user && snapshot.isFetched,
  });
}
